import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { useCreateProject, useProjects, type Project } from '../lib/projects';

export function ProjectsListPage() {
  const projects = useProjects();
  const createProject = useCreateProject();
  const [name, setName] = useState('');

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await createProject.mutateAsync(name.trim());
    setName('');
  }

  const sorted = useMemo(() => projects.data ?? [], [projects.data]);

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(79,70,229,0.22),transparent_32rem),#090A0F]">
      <div className="px-6 py-4 border-b border-border-color bg-background-dark/80 backdrop-blur sticky top-0 z-10">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          <span className="text-accent-cyan">Bubbles</span>
          <span className="mx-2 opacity-50">//</span>
          <span>Your brands</span>
        </p>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-10 space-y-8">
        <section className="grid lg:grid-cols-[1.15fr_0.85fr] gap-5 items-stretch">
          <div className="rounded-[2rem] border border-border-color bg-surface/80 p-4 md:p-8 overflow-hidden relative shadow-2xl shadow-black/20">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
            <div className="relative">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-4">Multi-brand content agency</p>
              <h1 className="font-display text-2xl md:text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
                One agency for every brand you operate.
              </h1>
              <p className="mt-5 text-base text-muted max-w-2xl leading-relaxed">
                Brief each brand once. Your strategy team learns the voice. Your content team drafts on demand. You sign off.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
                <Phase label="Strategy" text="Brand memory" />
                <Phase label="Content" text="On-demand drafts" />
                <Phase label="Posts" text="Ready to publish" />
              </div>
            </div>
          </div>

          <form onSubmit={onCreate} className="rounded-[2rem] border border-border-color bg-surface p-4 md:p-6 flex flex-col justify-between gap-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted mb-3">Add a brand</p>
              <h2 className="font-display text-2xl font-bold tracking-tight">Bring a new brand into the studio.</h2>
              <p className="mt-2 text-sm text-muted leading-relaxed">
                Each brand gets its own brief, its own strategy team, and its own content team. Give the brand a name to get started.
              </p>
            </div>
            <div className="space-y-3">
              <input
                id="new-project-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Brand name"
                className="w-full bg-background-dark border border-border-color rounded-xl px-4 py-3 text-sm text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
              />
              <button
                type="submit"
                disabled={createProject.isPending || !name.trim()}
                className="btn-bracket w-full bg-primary text-white px-4 py-3 hover:bg-primary/90 disabled:opacity-50 transition-colors rounded-xl"
              >
                {createProject.isPending ? 'Creating' : 'Create brand'}
              </button>
            </div>
          </form>
        </section>

        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Your brands</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-1">
                {projects.isLoading ? 'Loading' : `${sorted.length} ${sorted.length === 1 ? 'brand' : 'brands'}`} · role-aware access
              </p>
            </div>
          </div>

          {projects.isLoading && <p className="font-mono text-xs uppercase tracking-wider text-muted">Loading brands…</p>}

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sorted.map((p) => <BrandCard key={p.id} project={p} />)}
          </div>
        </section>
      </main>
    </div>
  );
}

function Phase({ label, text }: { label: string; text: string }) {
  return (
    <div className="rounded-2xl border border-border-color bg-background-dark/70 p-4">
      <p className="font-display text-lg font-bold">{label}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted">{text}</p>
    </div>
  );
}

function BrandCard({ project }: { project: Project }) {
  const channels = project.channels.join(' + ');
  return (
    <Link
      href={`/projects/${project.id}`}
      className="group relative block rounded-[1.5rem] border border-border-color bg-surface p-5 hover:border-accent-cyan/70 hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-1 bubbles-gradient-bg opacity-70" />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-2xl font-bold tracking-tight truncate">{project.name}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted truncate">{project.slug}</p>
        </div>
        <StatusPill status={project.status} />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-2 text-xs">
        <Meta label="Role" value={project.role === 'owner' ? 'Owner' : 'Co-creator'} />
        <Meta label="Channels" value={channels || 'Not set'} />
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-border-color pt-4">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted">Open workspace</span>
        <span className="material-symbols-outlined text-accent-cyan group-hover:translate-x-1 transition-transform">arrow_forward</span>
      </div>
    </Link>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-background-dark border border-border-color px-3 py-2">
      <p className="font-mono text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-1 text-text-primary truncate capitalize">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: 'active' | 'paused' | 'archived' }) {
  const variant =
    status === 'active' ? 'status-pill-emerald' : status === 'paused' ? 'status-pill-amber' : 'status-pill-muted';
  return <span className={`status-pill ${variant}`}>{status}</span>;
}
